/**
 * Media URL Helpers
 *
 * Provides unified URL generation for media assets.
 * All assets are served from GCS bucket - single source of truth for both
 * local development and production.
 */

// GCS bucket URL (required for all environments)
const MEDIA_BUCKET_URL = import.meta.env.VITE_MEDIA_BUCKET_URL || 'https://media.polymyth.quest';

/**
 * Get URL for a static asset (videos, images, staff-avatars, etc.)
 *
 * @param path - Path relative to static directory (e.g., "videos/intro.mp4")
 * @returns Full URL for the asset
 */
export function getStaticAssetUrl(path: string): string {
  return `${MEDIA_BUCKET_URL}/static/${path}`;
}

/**
 * Get URL for a video file
 *
 * @param filename - Video filename (e.g., "intro.mp4")
 * @returns Full URL for the video
 */
export function getVideoUrl(filename: string): string {
  return getStaticAssetUrl(`videos/${filename}`);
}

/**
 * Get URL for a staff avatar
 *
 * @param name - Avatar filename (e.g., "tavian.webp")
 * @returns Full URL for the avatar
 */
export function getStaffAvatarUrl(name: string): string {
  return getStaticAssetUrl(`staff-avatars/${name}`);
}

/**
 * Get URL for a staff portrait (full-size character art).
 *
 * Derives the portrait filename from the avatar filename by swapping the
 * `-avatar` suffix for `-portrait`. Used to open the enlarged portrait
 * modal when a staff avatar is clicked.
 *
 * @param avatarName - Avatar filename (e.g., "tavian-avatar.webp")
 * @returns Full URL for the portrait asset
 */
export function getStaffPortraitUrl(avatarName: string): string {
  const portraitName = avatarName.replace(/-avatar(\.[^.]+)$/, '-portrait$1');
  return getStaticAssetUrl(`staff-avatars/${portraitName}`);
}

/**
 * Get URL for a lobby NPC art image
 *
 * @param filename - Art filename (e.g., "aldric.webp")
 * @returns Full URL for the NPC art
 */
export function getLobbyNpcArtUrl(filename: string): string {
  return getStaticAssetUrl(`lobby-npc-art/${filename}`);
}

/**
 * Get URL for a module media file (hero images, maps, etc.)
 *
 * @param moduleId - Module ID/slug (e.g., "wolves_of_welton")
 * @param filename - Media filename (e.g., "hero_gen.webp")
 * @returns Full URL for the module media
 */
export function getModuleMediaUrl(moduleId: string, filename: string): string {
  return getStaticAssetUrl(`modules/${moduleId}/${filename}`);
}

/**
 * Get URL for a module hero image
 *
 * @param moduleId - Module ID/slug
 * @returns Full URL for the hero image
 */
export function getModuleHeroUrl(moduleId: string): string {
  return getModuleMediaUrl(moduleId, 'hero_gen.webp');
}

/**
 * Get URL for a module hero video (if available)
 *
 * @param moduleId - Module ID/slug
 * @returns Full URL for the hero video
 */
export function getModuleHeroVideoUrl(moduleId: string): string {
  return getModuleMediaUrl(moduleId, 'hero.mp4');
}

/**
 * Resolve a raw module media path as returned by the API (e.g.
 * "data/modules/{id}/hero_gen.webp") to a full GCS URL. Also handles a bare
 * video filename and an already-absolute URL.
 *
 * @param path - Raw path/filename from a `Module.heroImage`/`heroVideo` field
 * @returns Full URL for the asset
 */
export function resolveModuleMediaPath(path: string): string {
  if (path.startsWith('https://') || path.startsWith('http://')) {
    return path;
  }
  const moduleMatch = path.match(/^data\/modules\/([^/]+)\/(.+)$/);
  if (moduleMatch) {
    const [, moduleId, filename] = moduleMatch;
    return getModuleMediaUrl(moduleId, filename);
  }
  if (path.endsWith('.mp4') || path.endsWith('.webm')) {
    return getVideoUrl(path);
  }
  return getStaticAssetUrl(path);
}

/**
 * Get URL for a dynamic/generated image (character portraits, scene backgrounds, etc.)
 * These are generated by the backend and stored in GCS.
 *
 * @param url - URL or path from backend
 * @returns Full URL for the dynamic image
 */
export function getDynamicImageUrl(url: string): string {
  // If it's already a full URL (GCS), return as-is
  if (url.startsWith('https://') || url.startsWith('http://')) {
    return url;
  }

  // If it's a relative dynamic path (e.g., "dynamic/characters/...")
  if (url.startsWith('dynamic/')) {
    return `${MEDIA_BUCKET_URL}/${url}`;
  }

  // For other relative paths, assume it's under dynamic/
  return `${MEDIA_BUCKET_URL}/dynamic/${url}`;
}

/**
 * Get URL for a UI image
 *
 * @param filename - Image filename (e.g., "logo.webp")
 * @returns Full URL for the image
 */
export function getImageUrl(filename: string): string {
  return getStaticAssetUrl(`images/${filename}`);
}

/**
 * Check if we're using GCS storage (always true now - single SOT)
 * @deprecated No longer needed - always using GCS
 */
export function isUsingGcs(): boolean {
  return true;
}

/**
 * Get the base media bucket URL
 */
export function getMediaBucketUrl(): string {
  return MEDIA_BUCKET_URL;
}

/**
 * Default player avatar — hooded wanderer (Gemini-generated)
 * Used when player doesn't have a character yet
 */
export const DEFAULT_PLAYER_AVATAR = '/default-player-avatar.webp';
